import {
  Button,
  createStyles,
  makeStyles,
  TextField,
  Theme,
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import {
  DATA_TYPES,
  FONTS,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
} from "../../constants";
import { stateType } from "../../state";
import {
  getDefaultPersona,
  makePrediction,
  PERSONA_IMAGES,
} from "./PersonaPredictionBox";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    boxTitle: {
      fontFamily: FONTS,
      fontSize: "20px",
      margin: "20px",
      color: "rgba(0,0,0,0.7)",
      fontWeight: "bold",
    },
    inputsContainer: {
      display: "flex",
      color: "rgba(0,0,0,0.5)",
      flexDirection: "row",
      marginLeft: "20px",
      width: "350px",
      marginTop: "20px",
      marginBottom: "30px",
    },
    dropdownTitle: {
      fontFamily: FONTS,
      marginLeft: "0px",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      width: "120px",
    },
    avatarsSelectContainer: { marginLeft: "0px", marginBottom: "30px" },
    avatarsRow: {},
    avatarIcon: {
      width: "70px",
      border: "solid",
      borderWidth: "0px",
      borderRadius: "10px",
      marginLeft: "20px",
      marginTop: "20px",
      cursor: "pointer",
    },
    makePredButton: {
      fontFamily: FONTS,
      fontWeight: "bold",
      width: "100px",
      height: "40px",
      marginLeft: "20px",
      marginRight: "20px",
      backgroundColor: SECONDARY_COLOR,
    },
    avatarSelectContainer: {
      display: "flex",
      flexDirection: "column",
      margin: "20px",
    },
    avatarRow: { display: "flex", flexDirection: "row" },
    avatarSelect: {
      marginLeft: "8px",
      marginRight: "8px",
      marginTop: "5px",
      width: "70px",
      height: "70px",
    },
    avatarBorder: {
      border: "solid",
      borderRadius: "10px",
      borderColor: "white",
      borderWidth: "3px",
      "&:hover": {
        border: "solid",
        borderRadius: "10px",
        borderWidth: "3px",
        borderColor: "rgba(0,0,0,0.6)",
      },
      cursor: "pointer",
    },
    avatarBorderSelected: {
      border: "solid",
      borderRadius: "10px",
      borderColor: PRIMARY_COLOR,
      borderWidth: "3px",
      cursor: "pointer",
      "&:hover": {
        border: "solid",
        borderRadius: "10px",
        borderWidth: "3px",
        borderColor: "rgba(0,0,0,0.6)",
      },
    },
  })
);

function BoxTitle() {
  function Line() {
    return (
      <div
        style={{
          width: "100%",
          height: "1px",
          backgroundColor: "rgba(0,0,0,0.2)",
          position: "relative",
          marginTop: "-10px",
        }}
      ></div>
    );
  }
  const classes = useStyles();
  return (
    <div style={{ width: "400px" }}>
      <div className={classes.boxTitle}>Create a New Persona</div>
      <Line></Line>
    </div>
  );
}

function AvatarSelect({ state }: stateType) {
  const classes = useStyles();
  const avatarsPerRow = 4;

  let allChunks = [];
  for (let i = 0; i < PERSONA_IMAGES.length; i += avatarsPerRow) {
    allChunks.push(PERSONA_IMAGES.slice(i, i + avatarsPerRow));
  }

  return (
    <div className={classes.avatarSelectContainer}>
      <div
        style={{
          fontFamily: FONTS,
          marginLeft: "10px",
          marginBottom: "10px",
        }}
      >
        Persona Avatar
      </div>
      {allChunks.map((imgRow, ri) => (
        <div className={classes.avatarRow}>
          {imgRow.map((imgLoc, i) => (
            <div
              className={
                ri * avatarsPerRow + i === state.personaAvatarSelected
                  ? classes.avatarBorderSelected
                  : classes.avatarBorder
              }
              onClick={() =>
                state.setPersonaAvatarSelected(ri * avatarsPerRow + i)
              }
            >
              <img className={classes.avatarSelect} src={imgLoc}></img>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function PersonaPopup({ state }: stateType) {
  const classes = useStyles();

  function handleClick(e) {
    if (state.personaName.length === 0) {
      return;
    }

    let cols = state.saveState.dataset.columns.filter(
      (col) =>
        col.columnName != state.saveState.modelTraining.valueToPredict &&
        col.columnType !== DATA_TYPES.unusable
    );
    let persona = undefined;
    if (state.editingPersona === -1) {
      persona = getDefaultPersona(cols);
    } else {
      persona =
        state.saveState.personaPrediction.personas[
          state.saveState.personaPrediction.personaselected
        ].predictorValues;
    }

    let tmp = state.saveState.personaPrediction.personas;
    let pred = makePrediction(state, persona, state.saveState.savedModels);
    let newPersona = {
      name: state.personaName,
      avatar: PERSONA_IMAGES[state.personaAvatarSelected],
      predictorValues: persona,
      predicted: pred,
    };
    if (state.editingPersona !== -1) {
      tmp[state.editingPersona] = newPersona;
    } else {
      tmp.push(newPersona);
    }

    state.saveState.personaPrediction.personas = tmp;
    state.saveState.personaPrediction.personaselected = tmp.length - 1;
    state.setSaveState(state.saveState);
    state.setIsPersonaPopupOpen(false);
    state.setSaveStateModified(true);
  }

  function handleNameChange(event) {
    const personaName = event.target.value.toString();
    state.setPersonaName(personaName);
  }

  return (
    <Dialog
      open={state.isPersonaPopupOpen}
      onClose={() => {
        state.setIsPersonaPopupOpen(false);
      }}
      maxWidth="lg"
    >
      <div
        style={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <BoxTitle></BoxTitle>
        <AvatarSelect state={state}></AvatarSelect>
        <div
          style={{
            fontFamily: FONTS,
            marginLeft: "30px",
            marginRight: "auto",
            marginBottom: "-5px",
            marginTop: "10px",
          }}
        >
          Persona Name
        </div>
        <div className={classes.inputsContainer}>
          <TextField
            variant="outlined"
            size="small"
            value={state.personaName}
            onChange={handleNameChange}
            inputProps={{ maxLength: 20 }}
            style={{ width: "400px", marginLeft: "-10px" }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.makePredButton}
            style={{
              fontFamily: FONTS,
              fontWeight: "bold",
              width: "160px",
              height: "40px",
              backgroundColor: PRIMARY_COLOR,
            }}
            onClick={handleClick}
          >
            {state.editingPersona === -1 ? "Create" : "Apply"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
